<template>
  <section class="dd-crypto">
    <div class="dd-wall-card dd-crypto__card">
      <header class="dd-crypto__header">
        <h2 class="dd-crypto__title">
          {{ $t('crypto_subscription.transfer_using_the_following_details', { currency: currencySymbol, name: levelName }) }}
        </h2>
        <p class="dd-crypto__subtitle">
          {{ $t('crypto_subscription.subtitle') }}
        </p>
      </header>

      <div class="dd-crypto__grid">
        <div class="dd-crypto__col">
          <div class="dd-crypto__field" @click="copyAmount">
            <label class="dd-crypto__label">{{ $t('crypto_payment.amount') }}</label>
            <div class="dd-crypto__input-group">
              <input
                ref="inputAmount"
                type="text"
                class="form-control dd-crypto__input"
                :value="amount || ''"
                readonly
              />
            </div>
            <span v-if="amountCopied" class="dd-crypto__copied">{{ $t('crypto_payment.amount_copied') }}</span>
          </div>

          <div class="dd-crypto__field" @click="copyAddress">
            <label class="dd-crypto__label">{{ $t('crypto_payment.to_the_address') }}</label>
            <div class="dd-crypto__input-group">
              <input
                ref="inputAddress"
                type="text"
                class="form-control dd-crypto__input"
                :value="address || ''"
                readonly
              />
            </div>
            <span v-if="addressCopied" class="dd-crypto__copied">{{ $t('crypto_payment.address_copied') }}</span>
          </div>

          <div class="dd-crypto__qr" v-if="qrCode">
            <img :src="qrCode" alt="QR code" />
          </div>
        </div>

        <div class="dd-crypto__col">
          <div class="dd-crypto__wallet" v-if="paymentURL">
            <div class="dd-crypto__note">
              {{ $t('crypto_payment.if_you_have_the_wallet_app_installed', { currency: currencySymbol }) }}
            </div>
            <a :href="paymentURL" class="btn-orange">
              {{ $t('crypto_payment.pay_in_the_app') }}
            </a>
          </div>

          <div class="dd-crypto__status">
            <span class="dd-pill">{{ statusLabel }}</span>
          </div>

          <div class="dd-crypto__description">
            <span v-if="pendingInfo">
              {{ $t('crypto_subscription.pending_upgrade', { name: pendingInfo.nextLevelName || pendingInfo.next_level_name || '' , time: remainingTime }) }}
            </span>
            <span v-else>
              {{ $t('crypto_subscription.description') }}
            </span>
          </div>

          <div class="dd-crypto__footer">
            <div class="dd-crypto__footer-buttons">
              <button
                type="button"
                class="dd-crypto__back"
                @click="cancelPayment"
                :disabled="canceling"
              >
                {{ $t('crypto_subscription.cancel_payment') }}
              </button>
              <button
                type="button"
                class="dd-crypto__back"
                @click="closePanel"
              >
                {{ $t('crypto_subscription.go_back') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {logWarn, logError} from '@/helpers';

export default {
  name: 'SubscriptionCryptoPaymentPanel',
  data() {
    return {
      amountCopied: false,
      addressCopied: false,
      copyTimers: [],
      canceling: false,
    };
  },
  computed: {
    currencySymbol() {
      return this.$store.getters.selectedCryptoCurrencySymbol || '';
    },
    levelName() {
      const info = this.pendingInfo;
      if (info && (info.nextLevelName || info.next_level_name)) {
        return info.nextLevelName || info.next_level_name;
      }
      const current = this.$store.getters.currentLevel;
      return current && current.name ? current.name : '';
    },
    amount() {
      return this.$store.getters.amount;
    },
    address() {
      return this.$store.getters.paymentAddress;
    },
    qrCode() {
      return this.$store.getters.paymentQRCode;
    },
    paymentURL() {
      return this.$store.getters.paymentURL;
    },
    pendingInfo() {
      return this.$store.state.pendingUpgradeInfo;
    },
    remainingTime() {
      if (!this.pendingInfo) return '';
      let seconds = this.pendingInfo.remainingSeconds || 0;
      if (!seconds && this.pendingInfo.expiresAt) {
        const now = Date.now();
        const exp = new Date(this.pendingInfo.expiresAt).getTime();
        const diffMs = Math.max(exp - now, 0);
        seconds = Math.floor(diffMs / 1000);
      }
      if (!seconds) return '';
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const hLabel = this.$t('crypto_subscription.time_hours_short');
      const mLabel = this.$t('crypto_subscription.time_minutes_short');
      return `${hours}${hLabel} ${mins}${mLabel}`;
    },
    statusLabel() {
      const mode = this.$store.getters.cryptocurrencyPaymentMode;
      if (mode === 'awaiting_confirmation') {
        return this.$t('crypto_payment.awaiting_confirmation');
      }
      return this.$t('crypto_payment.awaiting_payment');
    },
  },
  methods: {
    copyAmount() {
      this.copyToClipboard(this.amount, 'amountCopied', 'inputAmount');
    },
    copyAddress() {
      this.copyToClipboard(this.address, 'addressCopied', 'inputAddress');
    },
    closePanel() {
      this.$store.dispatch('returnToSubscriptionFromCrypto');
    },
    async cancelPayment() {
      this.canceling = true;
      try {
        await this.$store.dispatch('cancelPendingSubscription');
      } finally {
        this.canceling = false;
      }
    },
    copyToClipboard(value, flag, refName) {
      if (!value) {
        return;
      }
      const input = this.$refs[refName];
      if (input) {
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          logWarn('Failed to focus input before clipboard copy', e);
        }
        try {
          input.select();
        } catch (e) {
          logWarn('Failed to select input text before clipboard copy', e);
        }
      }
      const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;
      if (clipboard && typeof clipboard.writeText === 'function') {
        clipboard.writeText(value).catch((err) => {
          logWarn('Clipboard API copy failed, falling back to execCommand', err);
          try {
            document.execCommand('copy');
          } catch (e) {
            logError('Fallback execCommand copy failed', e);
          }
        });
      } else {
        try {
          document.execCommand('copy');
        } catch (e) {
          logError('execCommand copy is not supported in this browser', e);
        }
      }
      this[flag] = true;
      const timer = setTimeout(() => {
        this[flag] = false;
      }, 2000);
      this.copyTimers.push(timer);
    },
  },
  beforeDestroy() {
    this.copyTimers.forEach(clearTimeout);
  },
};
</script>

<style scoped>
.dd-crypto__card {
  margin-top: 10px;
}
.dd-crypto__header {
  margin-bottom: 16px;
}
.dd-crypto__title {
  font-size: 20px;
  margin-bottom: 6px;
}
.dd-crypto__subtitle {
  color: #666;
  margin: 0;
}
.dd-crypto__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.dd-crypto__field {
  cursor: pointer;
}
.dd-crypto__label {
  font-weight: 700;
  margin-bottom: 4px;
  display: block;
}
.dd-crypto__input {
  font-size: 16px;
}
.dd-crypto__copied {
  color: var(--dd-accent);
  font-size: 12px;
}
.dd-crypto__qr {
  width: 100%;
  max-width: 260px;
  aspect-ratio: 1 / 1;
}
.dd-crypto__qr img {
  width: 100%;
  height: 100%;
  max-width: 260px;
  display: block;
  margin: 0;
  object-fit: contain;
}
.dd-crypto__wallet {
  margin-bottom: 12px;
}
.dd-crypto__status {
  margin-bottom: 12px;
}
.dd-crypto__description {
  color: #555;
  margin-bottom: 12px;
}
.dd-crypto__footer {
  display: flex;
  justify-content: flex-end;
}
.dd-crypto__footer-buttons {
  display: flex;
  gap: 10px;
}
.dd-crypto__back {
  border: none;
  background: transparent;
  color: var(--dd-accent);
  cursor: pointer;
}
@media (max-width: 768px) {
  .dd-crypto__grid {
    grid-template-columns: 1fr;
  }
}
</style>
